// Rating — 1..max star meter, read-only or editable. i18n-clean: the accessible
// `label`/`emptyLabel` strings arrive via props (caller translates); the per-star
// aria-label is the raw numeric value, caller-agnostic. Imports only from `./`.
import { useState } from 'react';
import { Icon } from './Icon.js';

export interface RatingProps {
  value: number | null;
  max?: number; // default 5
  onChange?: (value: number | null) => void;
  size?: number; // default 14
  label: string; // aria-label, caller translates
  emptyLabel?: string; // muted text when value is null and no onChange
}

export function Rating({ value, max = 5, onChange, size = 14, label, emptyLabel }: RatingProps) {
  const [hover, setHover] = useState(0);
  // Clamp to [0..max] so out-of-range input renders clamped instead of crashing.
  const clamped = value === null ? null : Math.max(0, Math.min(max, value));

  // Editable: one focusable button per star; clicking the current value clears.
  if (onChange) {
    const active = hover || clamped || 0;
    return (
      <div
        role="group"
        aria-label={label}
        style={{ display: 'inline-flex', gap: 2 }}
        onMouseLeave={() => setHover(0)}
      >
        {Array.from({ length: max }, (_, i) => {
          const n = i + 1;
          const filled = n <= active;
          return (
            <button
              key={n}
              type="button"
              aria-label={String(n)}
              onMouseEnter={() => setHover(n)}
              onClick={() => onChange(n === value ? null : n)}
              style={{
                background: 'transparent',
                border: 0,
                padding: 0,
                lineHeight: 0,
                cursor: 'pointer',
                color: filled ? 'var(--accent-1)' : 'var(--border-strong)',
                transition: 'color var(--dur-1) var(--ease-out)',
              }}
            >
              <Icon name="star" size={size} style={{ fill: filled ? 'currentColor' : undefined }} />
            </button>
          );
        })}
      </div>
    );
  }

  // Read-only, unrated: muted text when an emptyLabel is provided.
  if (clamped === null && emptyLabel !== undefined) {
    return <span style={{ fontSize: 'var(--fz-12)', color: 'var(--fg-3)' }}>{emptyLabel}</span>;
  }

  // Read-only stars. `role="img"` names the whole meter; the glyphs are decorative.
  const filledCount = clamped ?? 0;
  return (
    <span
      role="img"
      aria-label={label}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
    >
      {Array.from({ length: max }, (_, i) => {
        const filled = i < filledCount;
        return (
          <span key={i} aria-hidden style={{ display: 'inline-flex', lineHeight: 0 }}>
            <Icon
              name="star"
              size={size}
              // Inline `fill:currentColor` overrides lucide's `fill="none"` attribute
              // (CSS beats the presentation attribute) → a filled star; omit it for hollow.
              style={{
                fill: filled ? 'currentColor' : undefined,
                color: filled ? 'var(--accent-1)' : 'var(--border-strong)',
              }}
            />
          </span>
        );
      })}
    </span>
  );
}
